import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { CanvasPath } from "react-sketch-canvas";
import { useSearchParams } from "react-router-dom";
import useDbState from "../hooks/use-db-state";
import { getDbInstance } from "../hooks/use-db-state/utils/indexedDB";
import { notify } from "../hooks/use-db-state/utils/subscriptions";

const mkId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const normalizeSoort = (s?: string | null): PHOTO_SOORT => {
  const v = (s ?? '').toString().trim().toUpperCase();
  if (v.includes('E_FOTO')) return 'E_FOTO';
  if (v.includes('I_FOTO')) return 'I_FOTO';
  if (v.includes('FOTOPART')) return 'FOTOPART';
  if (v.includes('FOTOMACHINE')) return 'FOTOMACHINE';
  return 'FOTO';
};

interface InterventionInputContextType {
  resetInterventionInput: (targetCallId?: string) => Promise<void>;

  intervention: Intervention | null;
  setintervention: (intervention: Intervention | null) => void;
  email_addresses: string[];
  setemail_addresses: React.Dispatch<React.SetStateAction<string[]>>;
  photos: PHOTO[];
  setphotos: React.Dispatch<React.SetStateAction<PHOTO[]>>;
  photosHydrated: boolean;
  reason_visit: string | null;
  setreason_visit: (reason_visit: string | null) => void;
  solution_visit: string | null;
  setsolution_visit: (solution_visit: string | null) => void;
  solution_extra: string | null;
  setsolution_extra: (solution_extra: string | null) => void;

  follow_up: boolean | null;
  setfollow_up: (follow_up: boolean | null) => void;
  maintenance_contract: boolean | null;
  setmaintenance_contract: (maintenance_contract: boolean | null) => void;
  notification_dispatch: string | null;
  setnotification_dispatch: (notification_dispatch: string | null) => void;

  warranty: boolean | null;
  setwarranty: (warranty: boolean | null) => void;
  follow_up_reason: string | null;
  setfollow_up_reason: (follow_up_reason: string | null) => void;
  maintenance_contract_reason: string | null;
  setmaintenance_contract_reason: (maintenance_contract_reason: string | null) => void;

  used_parts: BOM[] | null;
  setused_parts: (used_parts: BOM[] | null) => void;
  used_spareparts: USED_SPAREPART[] | null;
  setused_spareparts: (used_spareparts: USED_SPAREPART[] | null) => void;
  other_used_parts: OTHER_PART[] | null;
  setother_used_parts: (other_used_parts: OTHER_PART[] | null) => void;

  start_visit: Date | null;
  setstart_visit: (start_visit: Date | null) => void;
  end_visit: Date | null;
  setend_visit: (end_visit: Date | null) => void;
  reason_time_edit: string | null;
  setreason_time_edit: (reason_time_edit: string | null) => void;
  kilometers: number | null;
  setkilometers: (kilometers: number | null) => void;
  distance_home: number | null;
  setdistance_home: (distance_home: number | null) => void;
  home_ride_time: string | null;
  sethome_ride_time: (home_ride_time: string | null) => void;

  name_signature: string | null;
  setname_signature: (name_signature: string | null) => void;
  signature_customer: string | null;
  setsignature_customer: (signature_customer: string | null) => void;
  signature_technician: string | null;
  setsignature_technician: (signature_technician: string | null) => void;
  signature_customer_paths: CanvasPath[] | null;
  setsignature_customer_paths: (signature_customer_paths: CanvasPath[] | null) => void;
  signature_technician_paths: CanvasPath[] | null;
  setsignature_technician_paths: (signature_technician_paths: CanvasPath[] | null) => void;

  btw_attest: KLANT_DOCUMENT[] | null;
  setbtw_attest: (btw_attest: KLANT_DOCUMENT[] | null) => void;

  start_kilometers: number | null;
  setstart_kilometers: (start_kilometers: number | null) => void;
  end_kilometers: number | null;
  setend_kilometers: (end_kilometers: number | null) => void;
  start_ride_time: string | null;
  setstart_ride_time: (start_ride_time: string | null) => void;
  end_ride_time: string | null;
  setend_ride_time: (end_ride_time: string | null) => void;

  triptime: TRIPTIME[] | null;
  settriptime: (triptime: TRIPTIME[] | null) => void;
  worktime: WORKTIME[] | null;
  setworktime: (worktime: WORKTIME[] | null) => void;
}

const InterventionInputContext = createContext<InterventionInputContextType | undefined>(undefined);

export const InterventionInputProvider = ({ children }: { children: React.ReactNode }) => {
  const [searchParams] = useSearchParams();

  const callid = searchParams.get('callid');
  const id = callid ? callid : '';
  const generalId = "general";

  const [intervention, setintervention] = useDbState<Intervention | null>('intervention', null, `mountq-${id}`, 'input');
  const [email_addresses, setemail_addresses] = useDbState<string[]>("email_addresses", [], `mountq-${id}`, 'input');
  // const [photos, setphotos] = useDbState<PHOTO[] | null>("photos", [], `mountq-${id}`, 'input');
  // const [photos, setphotos] = useDbState<PHOTO[]>("photos", [], `mountq-${id}`, 'input');
  const [reason_visit, setreason_visit] = useDbState<string | null>("reason_visit", '', `mountq-${id}`, 'input');
  const [solution_visit, setsolution_visit] = useDbState<string | null>("solution_visit", '', `mountq-${id}`, 'input');
  const [solution_extra, setsolution_extra] = useDbState<string | null>("solution_extra", '', `mountq-${id}`, 'input');

  const [warranty, setwarranty] = useDbState<boolean | null>("warranty", false, `mountq-${id}`, 'input');
  const [follow_up, setfollow_up] = useDbState<boolean | null>("follow_up", false, `mountq-${id}`, 'input');
  const [maintenance_contract, setmaintenance_contract] = useDbState<boolean | null>("maintenance_contract", false, `mountq-${id}`, 'input');
  const [notification_dispatch, setnotification_dispatch] = useDbState<string | null>("notification_dispatch", '', `mountq-${id}`, 'input');

  const [follow_up_reason, setfollow_up_reason] = useDbState<string | null>("follow_up_reason", '', `mountq-${id}`, 'input');
  const [maintenance_contract_reason, setmaintenance_contract_reason] = useDbState<string | null>("maintenance_contract_reason", '', `mountq-${id}`, 'input');

  const [used_parts, setused_parts] = useDbState<BOM[] | null>("used_parts", [], `mountq-${id}`, 'input');
  const [used_spareparts, setused_spareparts] = useDbState<USED_SPAREPART[] | null>("used_spareparts", [], `mountq-${id}`, 'input');
  const [other_used_parts, setother_used_parts] = useDbState<OTHER_PART[] | null>("other_used_parts", [], `mountq-${id}`, 'input');

  // const [start_visit, setstart_visit] = useDbState<Date | null>("start_visit", null, `mountq-${id}`, 'input');
  // const [end_visit, setend_visit] = useDbState<Date | null>("end_visit", null, `mountq-${id}`, 'input');
  const [reason_time_edit, setreason_time_edit] = useDbState<string | null>("reason_time_edit", '', `mountq-${id}`, 'input');

  const [start_kilometers, setstart_kilometers] = useDbState<number | null>("start_kilometers", 0, `mountq-${generalId}`, 'input');
  const [end_kilometers, setend_kilometers] = useDbState<number | null>("end_kilometers", 0, `mountq-${generalId}`, 'input');
  const [start_ride_time, setstart_ride_time] = useDbState<string | null>("start_ride_time", '', `mountq-${generalId}`, 'input');
  const [end_ride_time, setend_ride_time] = useDbState<string | null>("end_ride_time", '', `mountq-${generalId}`, 'input');

  const [kilometers, setkilometers] = useDbState<number | null>("kilometers", 0, `mountq-${generalId}`, 'input');
  const [distance_home, setdistance_home] = useDbState<number | null>("distance_home", 0, `mountq-${generalId}`, 'input');
  const [home_ride_time, sethome_ride_time] = useDbState<string | null>("home_ride_time", '', `mountq-${generalId}`, 'input');

  const [name_signature, setname_signature] = useDbState<string | null>("name_signature", '', `mountq-${id}`, 'input');
  const [signature_customer, setsignature_customer] = useDbState<string | null>("signature_customer", '', `mountq-${id}`, 'input');
  const [signature_technician, setsignature_technician] = useDbState<string | null>("signature_technician", '', `mountq-${id}`, 'input');
  const [signature_customer_paths, setsignature_customer_paths] = useDbState<CanvasPath[] | null>("signature_customer_paths", [], `mountq-${id}`, 'input');
  const [signature_technician_paths, setsignature_technician_paths] = useDbState<CanvasPath[] | null>("signature_technician_paths", [], `mountq-${id}`, 'input');

  const [btw_attest, setbtw_attest] = useDbState<KLANT_DOCUMENT[] | null>("btw_attest", null, `mountq-${id}`, 'input');
  const [worktime, setworktime] = useDbState<WORKTIME[] | null>("worktime", null, `mountq-${id}`, 'input');
  const [triptime, settriptime] = useDbState<TRIPTIME[] | null>("triptime", null, `mountq-${generalId}`, 'input');

  // === Guarded start_visit ===
  const [start_visit_raw, set_start_visit_raw] = useDbState<Date | null>(
    "start_visit",
    null,
    `mountq-${id}`,
    'input'
  );
  const [start_visit, _set_start_visit] = useState<Date | null>(null);
  const startLoaded = useRef(false);

  useEffect(() => {
    _set_start_visit(start_visit_raw);
    startLoaded.current = true;
  }, [start_visit_raw]);

  const setstart_visit = useCallback((ts: Date | null) => {
    _set_start_visit(ts);
    if (!startLoaded.current) return;
    if (ts == null) return;
    set_start_visit_raw(ts);
  }, [set_start_visit_raw]);

  // === Guarded end_visit ===
  const [end_visit_raw, set_end_visit_raw] = useDbState<Date | null>(
    "end_visit",
    null,
    `mountq-${id}`,
    'input'
  );
  const [end_visit, _set_end_visit] = useState<Date | null>(null);
  const endLoaded = useRef(false);

  useEffect(() => {
    _set_end_visit(end_visit_raw);
    endLoaded.current = true;
  }, [end_visit_raw]);

  const setend_visit = useCallback((ts: Date | null) => {
    _set_end_visit(ts);
    if (!endLoaded.current) return;
    if (ts == null) return;
    set_end_visit_raw(ts);
  }, [set_end_visit_raw]);

  // --- Guarded photos (avoid first empty snapshot race) ---
  const [photos_raw, setphotos_raw] = useDbState<PHOTO[]>("photos", [], `mountq-${id}`, 'input');
  const [photos, _setphotos] = useState<PHOTO[]>([]);
  const photosLoadedRef = useRef(false);
  const [photosHydrated, setPhotosHydrated] = useState(false);

  useEffect(() => {
    _setphotos((photos_raw ?? []).map(p => ({
      ...p,
      id: (p as any).id ?? mkId(),
      soort: normalizeSoort(p.soort),
    })));
    photosLoadedRef.current = true;
    setPhotosHydrated(true);
  }, [photos_raw]);

  const setphotos: React.Dispatch<React.SetStateAction<PHOTO[]>> = useCallback((updater) => {
    _setphotos(prevLocal => {
      const nextLocal = typeof updater === 'function'
        ? (updater as (s: PHOTO[]) => PHOTO[])(prevLocal)
        : updater;

      const toPersist = nextLocal.map(p => ({
        ...p,
        soort: normalizeSoort(p.soort),
        id: (p as any).id ?? mkId(),
      }));

      if (photosLoadedRef.current) {
        setphotos_raw(toPersist);
      }
      return toPersist;
    });
  }, [setphotos_raw]);


  async function resetInterventionInput(targetCallId?: string) {
    const callToClear = targetCallId ?? id;
    const dbName = `mountq-${callToClear}`;
    const storeName = "input";
    // 1) Write every key’s default back into the store:
    const db = await getDbInstance(dbName, storeName);
    const tx = db.transaction([storeName], "readwrite");
    const store = tx.objectStore(storeName);
    for (const [key, value] of Object.entries(defaultValues)) {
      store.put({ id: key, value });
      // notify any subscribers so your useDbState hooks update:
      notify(key, value);
    }
    // wait for the transaction to complete
    await new Promise<void>((resolve) => {
      tx.oncomplete = () => resolve();
    });

    // 2) If it’s the *current* callId, also clear all in-memory React state:
    if (!targetCallId || targetCallId === id) {
      setintervention(null)
      setemail_addresses([])
      setphotos([])
      setreason_visit(null)
      setsolution_visit(null)
      setsolution_extra(null)

      setwarranty(null)
      setfollow_up(null)
      setmaintenance_contract(null)
      setnotification_dispatch(null)
      setfollow_up_reason('')
      setmaintenance_contract_reason('')

      setused_parts(null)
      setused_spareparts(null)
      setother_used_parts(null)

      // setstart_visit(null)
      // setend_visit(null)
      _set_start_visit(null);
      _set_end_visit(null);
      set_start_visit_raw(null);
      set_end_visit_raw(null);
      setreason_time_edit(null)
      setkilometers(null)
      setdistance_home(null)
      sethome_ride_time(null)

      setname_signature(null)
      setsignature_customer(null)
      setsignature_technician(null)
      setsignature_customer_paths(null)
      setsignature_technician_paths(null)

      setbtw_attest(null)

      setstart_kilometers(null)
      setend_kilometers(null)
      setstart_ride_time(null)
      setend_ride_time(null)

      settriptime([])
      setworktime([])
    }

  }

  return (
    <InterventionInputContext.Provider value={{
      resetInterventionInput,

      intervention,
      setintervention,
      email_addresses,
      setemail_addresses,
      photos,
      setphotos,
      photosHydrated,
      reason_visit,
      setreason_visit,
      solution_visit,
      setsolution_visit,
      solution_extra,
      setsolution_extra,

      warranty,
      setwarranty,
      follow_up,
      setfollow_up,
      maintenance_contract,
      setmaintenance_contract,
      notification_dispatch,
      setnotification_dispatch,
      follow_up_reason,
      setfollow_up_reason,
      maintenance_contract_reason,
      setmaintenance_contract_reason,

      used_parts,
      setused_parts,
      used_spareparts,
      setused_spareparts,
      other_used_parts,
      setother_used_parts,

      start_visit,
      setstart_visit,
      end_visit,
      setend_visit,
      reason_time_edit,
      setreason_time_edit,
      kilometers,
      setkilometers,
      distance_home,
      setdistance_home,
      home_ride_time,
      sethome_ride_time,

      name_signature,
      setname_signature,
      signature_customer,
      setsignature_customer,
      signature_technician,
      setsignature_technician,
      signature_customer_paths,
      setsignature_customer_paths,
      signature_technician_paths,
      setsignature_technician_paths,

      btw_attest,
      setbtw_attest,

      start_kilometers,
      setstart_kilometers,
      end_kilometers,
      setend_kilometers,
      start_ride_time,
      setstart_ride_time,
      end_ride_time,
      setend_ride_time,

      triptime,
      settriptime,
      worktime, setworktime
    }}>
      {children}
    </InterventionInputContext.Provider>
  );
};

export const useInterventionInput = (): InterventionInputContextType => {
  const context = useContext(InterventionInputContext);

  if (!context) {
    throw new Error('useInterventionInput must be used within a InterventionInputProvider');
  }

  return context;
};

export type PHOTO_SOORT = "FOTOPART" | "I_FOTO" | "E_FOTO" | "FOTO" | "FOTOMACHINE"

export interface PHOTO {
  interventionid: number | string;
  soort: PHOTO_SOORT;
  customerdocid: number | string;
  docid: number | string;
  attestid: number | string;
  usedpartid: number | string;
  otherusedpartid: number | string;
  machineid: number | string;
  timestamp: string
  photo: string
}

export interface KLANT_DOCUMENT {
  interventionid: number | string;
  docid: number | string;
  customerid: number | string;
  machineid: number | string;
  sku: string;
  soort: "BTWATTEST" | "Bezoekrapport";
  type: string;
  language: string;
  price: number | string;
  created: string;
  sent: number;
  customername: string;
  usercreate: string;
  datecreate: string;
  handtekeningklant: string;
}

export interface USED_SPAREPART {
  interventionid: number | string;
  partid: number | string;
  amount: number | string;
  warranty: number | string;
  topprice: number | string;
  bom: number;
  machineid: string;
  timestamp: string;
  partname?: string
}

export interface OTHER_PART {
  interventionid: number | string;
  brand: string;
  sku: string;
  partname: string;
  description: string;
  amount: number | string;
  warranty: number | string;
  topprice: number | string;
  bom: number;
  machineid: string;
  timestamp: string;
}

export interface TRIPTIME {
  starttime: string;
  endtime: string;
  activity: string;
  technicianid: string;
  interventionid: string;
}

export interface WORKTIME {
  fromtime: string;
  totime: string;
}

// map every key in your store to its “empty” default
const defaultValues: Record<string, any> = {
  intervention: null,
  email_addresses: [],
  photos: [],
  reason_visit: null,
  solution_visit: null,
  solution_extra: null,
  warranty: null,
  follow_up: null,
  maintenance_contract: null,
  notification_dispatch: null,
  follow_up_reason: "",
  maintenance_contract_reason: "",
  used_parts: null,
  used_spareparts: null,
  other_used_parts: null,
  start_visit: null,
  end_visit: null,
  reason_time_edit: null,
  kilometers: null,
  distance_home: null,
  home_ride_time: null,
  name_signature: null,
  signature_customer: null,
  signature_technician: null,
  signature_customer_paths: null,
  signature_technician_paths: null,
  btw_attest: null,
  start_kilometers: null,
  end_kilometers: null,
  start_ride_time: null,
  end_ride_time: null,
  triptime: [],
  worktime: [],
};